import { IPDefaultButton, IPIconButton, IPModal, IPPrimaryButton, IPSeparator, IPText } from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import InfoMessage from 'components/Notification/InfoMessage';
import SuccesMessage from 'components/Notification/SuccesMessage';
import { globalStackTokensGapSmall } from 'globalStyles';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { decodeURIFromHtml } from 'utils/url';
import { IDriveItem } from '../../services/Graph/SharepointInterfaces';
import DirectoryExplorer, { ParentFolderInformation } from './DirectoryExplorer';

interface ISharepointFolderPickerProps {
  isOpen: boolean;
  parentFolder?: ParentFolderInformation;
  close: () => void;
  onSelect: (folder: IDriveItem) => void;
}

const getFolderPath = (folder: IDriveItem): string => {
  // parentReference.path looks like "/drives/{driveId}/root:/Sub1/Sub2"; absent for the drive root
  const parentPath = folder.parentReference?.path ?? '';
  const idx = parentPath.indexOf('root:');
  const relative = idx >= 0 ? decodeURIFromHtml(parentPath.substring(idx + 'root:'.length)) : '';

  return relative ? `${relative}/${folder.name ?? ''}` : folder.name ?? '';
};

const SharepointFolderPicker = (props: ISharepointFolderPickerProps) => {
  const [selectedFolder, setSelectedFolder] = useState<IDriveItem | undefined>(undefined);
  const { t } = useTranslation(['sharepoint']);

  //
  // Main render
  //
  return (
    <IPModal isOpen={props.isOpen} onDismiss={props.close} isBlocking>
      <IPStack
        verticalFill
        styles={{ root: { height: '80vh', width: 1000, minWidth: 320, maxWidth: '90vw', padding: 20 } }}
      >
        {/* Title and Close button*/}
        <IPStack.Item>
          <IPStack horizontal styles={{ root: { justifyContent: 'space-between' } }}>
            <IPStack.Item>
              <IPText variant="xLarge">{t('sharepoint:SharepointPicker.SelectFolder')}</IPText>
            </IPStack.Item>
            <IPStack.Item>
              <IPIconButton iconProps={{ iconName: 'CalculatorMultiply' }} onClick={() => props.close()} />
            </IPStack.Item>
          </IPStack>
        </IPStack.Item>
        <IPSeparator />
        <IPStack.Item grow>
          <DirectoryExplorer
            onSelectItems={(items?: IDriveItem[]) => {
              if (!items || !items.length || !items[0].folder) {
                setSelectedFolder(undefined);

                return;
              }
              setSelectedFolder(items[0]);
            }}
            invalidAuthToken={props.close}
            parentFolder={props.parentFolder}
            selectFolderMode
          />
        </IPStack.Item>
        <IPSeparator />
        <IPStack.Item>
          <IPStack horizontal horizontalAlign="space-between" verticalAlign="center" tokens={globalStackTokensGapSmall}>
            <IPStack.Item grow styles={{ root: { minWidth: 0 } }}>
              {selectedFolder?.root ? (
                <InfoMessage textSize="medium" message={t('sharepoint:SharepointPicker.RootSelectedWarning')} />
              ) : selectedFolder ? (
                <SuccesMessage
                  message={t('sharepoint:SharepointPicker.SelectedFolder', { path: getFolderPath(selectedFolder) })}
                />
              ) : (
                <InfoMessage textSize="medium" message={t('sharepoint:SharepointPicker.NoFolderSelected')} />
              )}
            </IPStack.Item>
            <IPStack.Item>
              <IPStack horizontal tokens={globalStackTokensGapSmall}>
                <IPPrimaryButton
                  text={t('translation:General.Button.Select')}
                  disabled={!selectedFolder}
                  onClick={() => {
                    if (selectedFolder) {
                      props.onSelect(selectedFolder);
                      props.close();
                    }
                  }}
                />
                <IPDefaultButton text={t('translation:General.Button.Cancel')} onClick={() => props.close()} />
              </IPStack>
            </IPStack.Item>
          </IPStack>
        </IPStack.Item>
      </IPStack>
    </IPModal>
  );
};

export default SharepointFolderPicker;
